// SMSF Rates and Thresholds Configuration
// Single source of truth for all SMSF-related rates, limits, and thresholds
// Imports from FinancialConfig.js and adds SMSF-specific configurations
// Last updated: October 2025

import { FinancialConfig } from './FinancialConfig.js';

export const SMSF_RATES_CONFIG = {
    // Financial Year Information (from loaded FinancialConfig metadata when available)
    get currentFinancialYear() {
        return FinancialConfig._metadata?.financialYear || '2026-27';
    },
    get lastUpdated() {
        return FinancialConfig._metadata?.indexationDate
            ? `Indexation ${FinancialConfig._metadata.indexationDate}`
            : 'July 1, 2026';
    },
    
    // Transfer Balance Cap and Related Limits (from FinancialConfig)
    transferBalanceCap: {
        get generalTBC() { return FinancialConfig.TRANSFER_BALANCE_CAP; },
        get definedBenefitIncomeCap() { return FinancialConfig.DEFINED_BENEFIT_INCOME_CAP; },
        get maxTaxFreeComponent() { return FinancialConfig.TRANSFER_BALANCE_CAP; }
    },
    
    // Contribution Caps (from FinancialConfig)
    contributionCaps: {
        concessional: {
            get limit() { return FinancialConfig.CONTRIBUTION_CAPS.CONCESSIONAL; },
            get taxRate() { return FinancialConfig.SUPER_TAX_RATES.CONTRIBUTIONS_TAX; }
        },
        nonConcessional: {
            get limit() { return FinancialConfig.CONTRIBUTION_CAPS.NON_CONCESSIONAL; },
            taxRate: 0.00
        },
        get totalSuperBalanceThreshold() { return FinancialConfig.CONTRIBUTION_CAPS.TOTAL_BALANCE_THRESHOLD; }
    },
    
    // Superannuation Guarantee (from FinancialConfig)
    superannuationGuarantee: {
        get rate() { return FinancialConfig.SUPERANNUATION_GUARANTEE.RATE; }, // 12%
        description: "Employer contribution rate"
    },
    
    // Tax Rates and Thresholds
    // NOTE: all values below are getters (not plain values evaluated once at import time) so
    // they stay in sync with FinancialConfig after the async getFinancialConfig() load merges
    // live financial-rates.json data into the same shared config object.
    taxRates: {
        division293: {
            get threshold() { return FinancialConfig.DIVISION_293.INCOME_THRESHOLD; }, // $250,000
            get additionalTaxRate() { return FinancialConfig.DIVISION_293.TAX_RATE; } // 15%
        },
        division296: {
            get threshold() { return FinancialConfig.DIVISION_296.THRESHOLD; },
            get veryLargeThreshold() { return FinancialConfig.DIVISION_296.VERY_LARGE_THRESHOLD; },
            get additionalTaxRate() { return FinancialConfig.DIVISION_296.TAX_RATE; },
            get additionalRateAboveVlsbt() { return FinancialConfig.DIVISION_296.ADDITIONAL_RATE_ABOVE_VLSBT; },
            get effectiveDate() { return FinancialConfig.DIVISION_296.EFFECTIVE_DATE; },
            get releaseAuthorityLimit() { return FinancialConfig.DIVISION_296.RELEASE_AUTHORITY_LIMIT; }
        },
        deathBenefits: {
            lumpSum: {
                under60: 0.22, // 22% for non-dependents under 60 (includes Medicare levy)
                over60: 0.17   // 17% for non-dependents over 60 (includes Medicare levy)
            },
            pension: {
                get under60() { return FinancialConfig.SUPER_TAX_RATES.WITHDRAWAL_TAX_UNDER_60; }, // 15%
                get over60() { return FinancialConfig.SUPER_TAX_RATES.WITHDRAWAL_TAX_OVER_60; }   // 0%
            }
        },
        excessContributions: {
            concessional: 0.47, // Top marginal tax rate
            nonConcessional: 0.47 // Top marginal tax rate
        },
        tbcBreach: 0.45, // Excess TBC tax rate
        inHouseAssetBreach: 0.45, // Penalty tax rate
        lrbaNonCompliance: 0.45 // Penalty tax rate
    },
    
    // LRBA (Limited Recourse Borrowing Arrangement) Limits (from FinancialConfig).
    // These are lending-practice/policy limits, not annually-indexed ATO thresholds, so
    // financial-rates.json does not carry them — they only change via a manual code update.
    lrbaLimits: {
        get maxLoanToValueRatio() { return FinancialConfig.SMSF.BORROWING.LRBA.MAXIMUM_LOAN_TO_VALUE; }, // 70%
        get minDeposit() { return FinancialConfig.SMSF.BORROWING.LRBA.MINIMUM_DEPOSIT; }, // 30%
        get maxLoanTerm() { return FinancialConfig.SMSF.BORROWING.LRBA.MAXIMUM_LOAN_TERM; }, // 30 years
        minLoanAmount: 100000, // $100,000 (practical minimum)
        minPropertyValue: 250000 // $250,000 (practical minimum for LRBA)
    },
    
    // In-House Asset Restrictions (from FinancialConfig)
    inHouseAssetLimits: {
        get maxPercentage() { return FinancialConfig.SMSF.COMPLIANCE.IN_HOUSE_ASSETS.MAXIMUM_PERCENTAGE; }, // 5%
        get relatedPartyLoansMaxPercentage() { return FinancialConfig.SMSF.COMPLIANCE.IN_HOUSE_ASSETS.MAXIMUM_PERCENTAGE; } // 5%
    },
    
    // Succession Planning Limits
    successionPlanning: {
        maxDeathBenefitNominations: 5,
        get minReversionaryPensionAge() { return FinancialConfig.MINIMUM_PENSION_STANDARDS.MINIMUM_PENSION_AGE; } // 60
    },
    
    // Administrative Penalties (in penalty units)
    penalties: {
        lateAnnualReturn: 210, // $210 per penalty unit (increases with delay)
        maxAdministrativePenalty: 13320, // $13,320 maximum
        maxCivilPenalty: 1110000 // $1,110,000 maximum
    },
    
    // Compliance Requirements (from FinancialConfig)
    compliance: {
        annualAuditRequired: true,
        annualReturnDueDate: "28 Feb", // Previous financial year
        memberStatementsRequired: true,
        investmentStrategyRequired: true,
        get recordKeepingYears() { return FinancialConfig.SMSF.ADMINISTRATION.RECORD_KEEPING.MINIMUM_RETENTION_PERIOD; } // 10 years
    },
    
    // Re-export full SMSF config from FinancialConfig for advanced use
    get fullSMSFConfig() { return FinancialConfig.SMSF; }
};

// Helper functions to get formatted values
export const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-AU', {
        style: 'currency',
        currency: 'AUD',
        minimumFractionDigits: 0,
        maximumFractionDigits: 0
    }).format(amount);
};

export const formatPercentage = (rate) => {
    return `${(rate * 100).toFixed(1)}%`;
};

// Get current rates for display
export const getCurrentRates = () => {
    return {
        transferBalanceCap: formatCurrency(SMSF_RATES_CONFIG.transferBalanceCap.generalTBC),
        concessionalLimit: formatCurrency(SMSF_RATES_CONFIG.contributionCaps.concessional.limit),
        nonConcessionalLimit: formatCurrency(SMSF_RATES_CONFIG.contributionCaps.nonConcessional.limit),
        tsbThreshold: formatCurrency(SMSF_RATES_CONFIG.contributionCaps.totalSuperBalanceThreshold),
        sgRate: formatPercentage(SMSF_RATES_CONFIG.superannuationGuarantee.rate),
        division293Threshold: formatCurrency(SMSF_RATES_CONFIG.taxRates.division293.threshold),
        lrbaMaxLvr: formatPercentage(SMSF_RATES_CONFIG.lrbaLimits.maxLoanToValueRatio),
        inHouseAssetLimit: formatPercentage(SMSF_RATES_CONFIG.inHouseAssetLimits.maxPercentage)
    };
};

// Validate rates are current
export const validateRatesCurrency = () => {
    const currentYear = new Date().getFullYear();
    const configYear = parseInt(SMSF_RATES_CONFIG.currentFinancialYear.split('-')[0]);
    
    if (currentYear > configYear) {
        console.warn(`SMSF rates may be outdated. Config year: ${configYear}, Current year: ${currentYear}`);
        return false;
    }
    return true;
};

export default SMSF_RATES_CONFIG;
